import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { makeStyles, tokens, Button, Card, CardHeader, Spinner, Text } from "@fluentui/react-components";
import {
  Open24Regular,
  SlideText24Regular,
  ArrowSync24Regular,
  Image24Regular,
  FullScreenMaximize24Regular,
  FullScreenMinimize24Regular,
} from "@fluentui/react-icons";
import { fetchCoverThumbnail, fetchPreviewUrl, isSafeWebUrl } from "../../services/powerpoint";
import { updateNote } from "../../services/db";

// ─── Session-level in-memory caches ───────────────────────────────────────────
// Keyed by noteId. Thumbnails survive navigation; preview URLs are short-lived
// (~minutes) so we only cache them for the mount and re-fetch on demand.
const _thumbCache = new Map<string, string>();

interface PptContent {
  webUrl?: string;
  name?: string;
  driveItemId?: string;
  thumbnailUrl?: string;
  syncedAt?: number;
}

/** Parse the Firestore `content` field for a powerpoint-link note. */
export function parsePptContent(raw: string | undefined): PptContent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.webUrl || parsed.driveItemId)) return parsed as PptContent;
    return null;
  } catch {
    return null;
  }
}

interface PowerPointLinkNoteProps {
  noteId: string;
  driveItemId: string;
  initialData?: string; // JSON stringified content from Firestore
  graphToken: string | null;
  noteLabel: string;
}

const useStyles = makeStyles({
  card: { marginBottom: "12px", width: "100%" },
  headerAction: { display: "flex", alignItems: "center", gap: "8px" },
  content: { padding: "0 12px 12px 12px" },
  media: {
    position: "relative",
    margin: "0 -12px 10px -12px",
    backgroundColor: "#1b1b1b",
    overflow: "hidden",
  },
  mediaFull: {
    position: "fixed",
    inset: "0",
    margin: "0",
    height: "100vh",
    width: "100vw",
    zIndex: 3000,
  },
  fill: { position: "absolute", inset: "0", width: "100%", height: "100%" },
  thumb: { objectFit: "contain" },
  viewer: { border: "none", display: "block" },
  overlay: {
    position: "absolute",
    inset: "0",
    backgroundColor: "#1b1b1b",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    zIndex: 2,
  },
  overlayText: { color: "#e5e7eb", fontSize: "12px", textAlign: "center", padding: "0 16px" },
  fsBtn: {
    position: "absolute",
    top: "6px",
    right: "6px",
    zIndex: 4,
    minWidth: "32px",
    backgroundColor: "rgba(0,0,0,0.55)",
    color: "#ffffff",
  },
  buttonRow: { display: "flex", gap: "6px" },
  errorText: { color: tokens.colorPaletteRedForeground1, fontSize: "12px" },
});

export const PowerPointLinkNote: React.FC<PowerPointLinkNoteProps> = ({
  noteId,
  driveItemId,
  initialData,
  graphToken,
  noteLabel,
}) => {
  const styles = useStyles();
  const stored = parsePptContent(initialData);

  const webUrl = stored?.webUrl || "";
  const deckName = stored?.name || "Presentation";

  const [thumbUrl, setThumbUrl] = useState<string | null>(
    () => _thumbCache.get(noteId) || stored?.thumbnailUrl || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Inline slide viewer (Part 2) ──
  const [showViewer, setShowViewer] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const fsBusyRef = useRef(false);

  const refreshThumb = async () => {
    if (!graphToken || !driveItemId) return;
    setLoading(true);
    setError(null);
    const url = await fetchCoverThumbnail(graphToken, driveItemId);
    if (url) {
      _thumbCache.set(noteId, url);
      setThumbUrl(url);
      updateNote(noteId, {
        content: JSON.stringify({ ...stored, thumbnailUrl: url, syncedAt: Date.now() }),
        attachmentType: "powerpoint-link",
      }).catch(() => { /* non-fatal */ });
    } else if (!thumbUrl) {
      setError("Preview unavailable. You can still open the deck in PowerPoint.");
    }
    setLoading(false);
  };

  useEffect(() => {
    // Depends on graphToken too: in the View dialog the token often arrives
    // asynchronously (after mount), so re-run once it's available or the cover
    // thumbnail never auto-loads.
    if (graphToken && driveItemId && !_thumbCache.has(noteId)) refreshThumb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, driveItemId, graphToken]);

  const loadPreview = async () => {
    if (!graphToken || !driveItemId) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setIframeLoaded(false);
    const url = await fetchPreviewUrl(graphToken, driveItemId);
    if (url) setPreviewUrl(url);
    else setPreviewError("Couldn’t load the slide viewer. Open in PowerPoint to view the deck.");
    setPreviewLoading(false);
  };

  const toggleViewer = async () => {
    if (showViewer) {
      setShowViewer(false);
      setIsFull(false);
      return;
    }
    setShowViewer(true);
    if (!graphToken) {
      setPreviewError("OneDrive isn’t connected. Connect it in the Margin Pro task pane (header → “Enable OneDrive”), then reopen this note to preview slides — or use “Open in PowerPoint.”");
      return;
    }
    await loadPreview(); // preview URLs expire; (re)fetch each time we open
  };

  const openDeck = () => {
    if (isSafeWebUrl(webUrl)) window.open(webUrl, "_blank", "noopener,noreferrer");
  };

  // Show a loading indicator from the moment Preview is clicked until the
  // iframe has actually painted its slides (fetch phase OR iframe-load phase).
  const showLoadingOverlay = showViewer && (previewLoading || (!!previewUrl && !iframeLoaded));

  const mediaHeight = showViewer ? "260px" : "150px";

  return (
    <Card className={styles.card}>
      <CardHeader
        header={<Text weight="semibold">{noteLabel}</Text>}
        description={<Text size={200}>{deckName}</Text>}
        action={
          <div className={styles.headerAction}>
            {(loading || previewLoading) && <Spinner size="extra-tiny" />}
            {!showViewer && (
              <Button
                appearance="subtle"
                icon={<ArrowSync24Regular />}
                onClick={(e) => { e.stopPropagation(); refreshThumb(); }}
                disabled={loading || !graphToken}
                title="Refresh preview"
              />
            )}
          </div>
        }
      />

      <div className={styles.content}>
        <div
          className={`${styles.media}${isFull ? " " + styles.mediaFull : ""}`}
          style={isFull ? undefined : { height: mediaHeight }}
        >
          {showViewer && previewUrl && (
            <iframe
              src={previewUrl}
              className={`${styles.fill} ${styles.viewer}`}
              title={`${deckName} slides`}
              allowFullScreen
              onLoad={() => setIframeLoaded(true)}
            />
          )}

          {showLoadingOverlay && (
            <div className={styles.overlay}>
              <Spinner size="small" />
              <span className={styles.overlayText}>Loading slides…</span>
            </div>
          )}

          {showViewer && !previewUrl && !previewLoading && (
            <div className={styles.overlay}>
              <SlideText24Regular style={{ fontSize: "28px", color: "#9ca3af" }} />
              <span className={styles.overlayText}>{previewError || "Preview unavailable"}</span>
            </div>
          )}

          {!showViewer && (
            thumbUrl ? (
              <img
                src={thumbUrl}
                alt={`${deckName} cover slide`}
                className={`${styles.fill} ${styles.thumb}`}
                onError={() => setThumbUrl(null)}
              />
            ) : (
              <div className={styles.overlay}>
                <SlideText24Regular style={{ fontSize: "32px", color: "#9ca3af" }} />
                <span className={styles.overlayText}>{loading ? "Loading preview…" : "PowerPoint"}</span>
              </div>
            )
          )}

          {showViewer && previewUrl && (
            <Button
              appearance="subtle"
              className={styles.fsBtn}
              icon={isFull ? <FullScreenMinimize24Regular /> : <FullScreenMaximize24Regular />}
              title={isFull ? "Exit full screen" : "Full screen"}
              onClick={(e) => {
                e.stopPropagation();
                if (fsBusyRef.current) return;
                fsBusyRef.current = true;
                setIsFull((f) => !f);
                setTimeout(() => { fsBusyRef.current = false; }, 150);
              }}
            />
          )}
        </div>

        {error && <div className={styles.errorText}>{error}</div>}

        <div className={styles.buttonRow}>
          <Button
            appearance="secondary"
            icon={showViewer ? <Image24Regular /> : <SlideText24Regular />}
            onClick={(e) => { e.stopPropagation(); toggleViewer(); }}
            disabled={previewLoading}
            title={showViewer ? "Show cover slide" : "Page through slides here"}
          >
            {previewLoading ? "Loading…" : showViewer ? "Show cover" : "Preview slides"}
          </Button>
          <Button
            appearance="primary"
            icon={<Open24Regular />}
            style={{ backgroundColor: "#2f8570", flex: 1 }}
            disabled={!webUrl}
            onClick={(e) => { e.stopPropagation(); openDeck(); }}
          >
            Open in PowerPoint
          </Button>
        </div>
      </div>
    </Card>
  );
};
